// Phase 2.2 (hh-e5n) — single-item loader.
//
// There is no GET /api/v1/items/{id} endpoint, so a single item is loaded by
// walking GET /api/v1/properties/tree (the same approach the forms feature
// uses via usePropertyTree). The tree carries `isOrphan` and `containerId` on
// every ItemNode, plus enough structure to derive a human-readable location
// label and to detect whether the item is itself a container (case-14).

import { useEffect, useState } from 'preact/hooks';
import { properties, humanizeError } from '../../api';
import type {
  ItemNode,
  PropertyTreeNode,
  PropertyTreeResponse,
} from '../../api/endpoints/properties.js';

export type ItemStatus = 'loading' | 'ready' | 'not-found' | 'error';

/** A located item: the ItemNode plus a derived location label. */
export interface LocatedItem {
  readonly node: ItemNode;
  /** Human-readable location, e.g. "Дача → Спальня". Empty when orphan. */
  readonly locationLabel: string;
  /** True when at least one other tree item has `containerId === this.id`. */
  readonly isContainer: boolean;
  /** Count of items nested directly inside this item. */
  readonly containedCount: number;
}

export interface ItemState {
  readonly status: ItemStatus;
  readonly item: LocatedItem | null;
  /** Full tree, reused by MoveItemSheet so it need not refetch. */
  readonly tree: readonly PropertyTreeNode[];
  readonly error: string | null;
  reload(): void;
}

function describeError(err: unknown): string {
  return humanizeError(err, 'load');
}

/** Walk the tree and locate `itemId`, deriving its location label. */
function locate(
  tree: readonly PropertyTreeNode[],
  itemId: string,
): { node: ItemNode; label: string } | null {
  for (const property of tree) {
    for (const direct of property.items) {
      if (direct.id === itemId) return { node: direct, label: property.name };
    }
    for (const room of property.rooms) {
      for (const item of room.items) {
        if (item.id === itemId) {
          return { node: item, label: `${property.name} → ${room.name}` };
        }
      }
    }
  }
  return null;
}

/** Count items across the whole tree whose `containerId` is `itemId`. */
function countContained(tree: readonly PropertyTreeNode[], itemId: string): number {
  let n = 0;
  for (const property of tree) {
    for (const item of property.items) {
      if (item.containerId === itemId) n += 1;
    }
    for (const room of property.rooms) {
      for (const item of room.items) {
        if (item.containerId === itemId) n += 1;
      }
    }
  }
  return n;
}

export function useItem(itemId: string): ItemState {
  const [status, setStatus] = useState<ItemStatus>('loading');
  const [item, setItem] = useState<LocatedItem | null>(null);
  const [tree, setTree] = useState<readonly PropertyTreeNode[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError(null);
    properties
      .getPropertyTree()
      .then((res: PropertyTreeResponse) => {
        if (cancelled) return;
        setTree(res.properties);
        const found = locate(res.properties, itemId);
        if (found === null) {
          setItem(null);
          setStatus('not-found');
          return;
        }
        const containedCount = countContained(res.properties, itemId);
        setItem({
          node: found.node,
          locationLabel: found.node.isOrphan ? '' : found.label,
          isContainer: containedCount > 0,
          containedCount,
        });
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(describeError(err));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [itemId, nonce]);

  return {
    status,
    item,
    tree,
    error,
    reload: () => setNonce((n) => n + 1),
  };
}
